import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Ticket, Building2, Sparkles, Radio, Zap } from "lucide-react";
import Reveal from "../components/Reveal";

const features = [
  "Hyperlocal event discovery across cities",
  "Venue partnerships and digital promotion",
  "Integrated ticketing and RSVP management",
  "Brand activation opportunities at events",
  "Out-of-home promotional tie-ins for events",
  "OOH + digital cross-channel campaign integration",
];

const pkStats = [
  { value: "100+", label: "Events Listed" },
  { value: "15+", label: "Cities Active" },
  { value: "OOH+", label: "Media Integration" },
];

const heroImg = "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7";
const uniqueImg = "https://images.pexels.com/photos/7269208/pexels-photo-7269208.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const icons = [MapPin, Building2, Ticket, Sparkles, Radio, Zap];

const PartyKahanHai = () => {
  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ minHeight: "78vh", display: "flex", alignItems: "center" }}>
        <div className="absolute inset-0">
          <img src={heroImg} alt="Nightlife event" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,13,0.75) 0%, rgba(11,11,13,0.9) 100%)" }} />
        </div>
        <div className="oo-container relative" style={{ paddingTop: 120, paddingBottom: 60 }}>
          <Reveal><span className="oo-eyebrow">Projects → Party Kahan Hai</span></Reveal>
          <Reveal delay={80}>
            <h1 className="font-display font-extrabold" style={{ fontSize: "clamp(40px,7vw,84px)", lineHeight: 1.02, marginTop: 20, letterSpacing: "-0.02em" }}>
              Party <span className="text-accent">Kahan Hai</span>
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p style={{ color: "var(--oo-muted)", fontSize: 20, marginTop: 18, fontWeight: 500 }}>India's Premier Party Discovery Platform</p>
          </Reveal>
          <Reveal delay={200}>
            <p style={{ color: "var(--oo-muted)", fontSize: 16, lineHeight: 1.7, maxWidth: 640, marginTop: 20 }}>
              Party Kahan Hai is OOHMA's consumer-facing platform designed to bridge the gap between event organisers, venues, and party-goers across India. A hyperlocal discovery and ticketing ecosystem that brings nightlife, events, and experiences to your fingertips.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURES */}
      <section className="oo-section">
        <div className="oo-container">
          <Reveal>
            <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(28px,4vw,44px)", letterSpacing: "-0.02em" }}>Platform <span className="text-accent">Features</span></h2>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" style={{ marginTop: 44 }}>
            {features.map((f, i) => {
              const Icon = icons[i % icons.length];
              return (
                <Reveal key={f} delay={(i % 3) * 80}>
                  <div className="oo-card h-full flex items-start gap-4" style={{ padding: 28 }}>
                    <div className="inline-flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: 12, background: "var(--oo-orange-soft)" }}>
                      <Icon size={20} className="text-accent" />
                    </div>
                    <p style={{ color: "var(--oo-text)", lineHeight: 1.6, fontSize: 15, fontWeight: 500 }}>{f}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section style={{ background: "var(--oo-bg-2)", padding: "64px 0" }}>
        <div className="oo-container">
          <div className="grid grid-cols-3 gap-6">
            {pkStats.map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <div className="text-center">
                  <div className="font-display font-extrabold text-accent" style={{ fontSize: "clamp(32px,5vw,60px)", lineHeight: 1 }}>{s.value}</div>
                  <div style={{ color: "var(--oo-muted)", fontSize: 14, marginTop: 10 }}>{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* UNIQUE */}
      <section className="oo-section">
        <div className="oo-container">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <div style={{ overflow: "hidden", borderRadius: 24, border: "1px solid var(--oo-border)" }}>
                <img src={uniqueImg} alt="Nightclub" className="w-full object-cover" style={{ aspectRatio: "4/3" }} />
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div>
                <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(28px,4vw,44px)", letterSpacing: "-0.02em" }}>What Makes It <span className="text-accent">Unique</span></h2>
                <p style={{ color: "var(--oo-muted)", fontSize: 16, lineHeight: 1.8, marginTop: 22 }}>
                  Party Kahan Hai leverages OOHMA's existing media infrastructure — bus shelters, LED screens, and school networks — to promote events at the grassroots level. It creates a unique loop: digital discovery backed by physical presence, making event promotions visible where people actually live and move.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ paddingBottom: 110 }}>
        <div className="oo-container text-center">
          <Reveal>
            <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(30px,4.5vw,52px)", letterSpacing: "-0.02em" }}>Partner With <span className="text-accent">Party Kahan Hai</span></h2>
            <Link to="/contact" className="btn-primary" style={{ marginTop: 30 }}>Get in Touch <ArrowUpRight size={18} /></Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default PartyKahanHai;
